import { TourConfig } from "@/types/tour"
import { standardOffset } from "./utils/floatingUIConfigs"

/**
 * Tour configuration for the Overview page
 */
export const overviewTour: TourConfig = {
   id: "overview_tour",
   humanReadableName: "Overview Tour",
   skippable: true,
   options: {
      useModalOverlay: true,
      defaultStepOptions: {
         classes: "q-card q-dark-transition shepherd-theme-quasar",
         arrow: true,
         scrollTo: { behavior: "smooth", block: "center" },
      },
   },
   steps: [
      {
         id: "welcome",
         title: "Welcome to the Overview",
         text: "This tour will guide you through the Overview page and explain its key features.",
         buttons: [
            {
               action: function () {
                  // Shepherd adds this method to the button context
                  if (this.next) return this.next()
                  return undefined
               },
               classes: "shepherd-button-primary q-ml-sm",
               text: "Next",
            },
         ],
      },
      {
         id: "date-picker",
         text: "Use this date picker to adjust the time range for the data displayed on this page. All the charts and metrics on this page will update to reflect your selected timeframe, giving you precise control over the data you're analyzing.",
         buttons: [
            {
               action: function () {
                  // Shepherd adds this method to the button context
                  if (this.back) return this.back()
                  return undefined
               },
               classes: "shepherd-button-secondary q-ml-sm",
               text: "Back",
            },
            {
               action: function () {
                  // Shepherd adds this method to the button context
                  if (this.next) return this.next()
                  return undefined
               },
               classes: "shepherd-button-primary q-ml-sm",
               text: "Next",
            },
         ],
         attachTo: {
            element: "#date-picker",
            on: "left",
         },
         floatingUIOptions: standardOffset,
      },
      {
         id: "conversion-lift-card",
         text: `This chart shows how much ezbot is helping your site perform better. Each bar represents the average conversion rate. The lines above and below (called "whiskers") show the 99% confidence interval—the range where we're 99% sure the true conversion rate falls. As more visitors interact with your site, the range shrinks, which means our estimate becomes more precise.`,
         buttons: [
            {
               action: function () {
                  // Shepherd adds this method to the button context
                  if (this.back) return this.back()
                  return undefined
               },
               classes: "shepherd-button-secondary q-ml-sm",
               text: "Back",
            },
            {
               action: function () {
                  // Shepherd adds this method to the button context
                  if (this.next) return this.next()
                  return undefined
               },
               classes: "shepherd-button-primary q-ml-sm",
               text: "Next",
            },
         ],
         attachTo: {
            element: "#conversion-lift-card",
            on: "right",
         },
         floatingUIOptions: standardOffset,
      },
      {
         id: "additional-conversions-card",
         text: "This card shows how many extra conversions ezbot has driven compared to your site's default experience. It compares actual results with ezbot to an estimate of what would have happened without it that assumes the default experience, highlighting the added value ezbot delivers in plain numbers.",
         buttons: [
            {
               action: function () {
                  // Shepherd adds this method to the button context
                  if (this.back) return this.back()
                  return undefined
               },
               classes: "shepherd-button-secondary q-ml-sm",
               text: "Back",
            },
            {
               action: function () {
                  // Shepherd adds this method to the button context
                  if (this.next) return this.next()
                  return undefined
               },
               classes: "shepherd-button-primary q-ml-sm",
               text: "Next",
            },
         ],
         attachTo: {
            element: "#additional-conversions-card",
            on: "left",
         },
         floatingUIOptions: standardOffset,
      },
      {
         id: "top-converter-card",
         text: `This card shows the most common experiences ezbot is delivering to your visitors and how they're performing. The chart highlights the top five combinations served most often. It's a quick way to see which versions are getting the most exposure. Hover over each slice to view its conversion rate.`,
         buttons: [
            {
               action: function () {
                  // Shepherd adds this method to the button context
                  if (this.back) return this.back()
                  return undefined
               },
               classes: "shepherd-button-secondary q-ml-sm",
               text: "Back",
            },
            {
               action: function () {
                  // Shepherd adds this method to the button context
                  if (this.next) return this.next()
                  return undefined
               },
               classes: "shepherd-button-primary q-ml-sm",
               text: "Next",
            },
         ],
         attachTo: {
            element: "#top-converter-card",
            on: "left",
         },
         floatingUIOptions: standardOffset,
      },
      {
         id: "graph-card",
         text: "This graph compares how your site is performing with and without ezbot over time. The purple line shows the average conversion rate for visitors who saw ezbot-optimized experiences. The grey line represents the holdback group's performance - visitors who saw the default version. This lets you track performance trends and see the impact of optimization.",
         buttons: [
            {
               action: function () {
                  // Shepherd adds this method to the button context
                  if (this.back) return this.back()
                  return undefined
               },
               classes: "shepherd-button-secondary q-ml-sm",
               text: "Back",
            },
            {
               action: function () {
                  if (this.next) return this.next()
                  return undefined
               },
               classes: "shepherd-button-primary q-ml-sm",
               text: "Next",
            },
         ],
         attachTo: {
            element: "#optimized-vs-default-graph-card",
            on: "top",
         },
         floatingUIOptions: standardOffset,
      },
      {
         id: "tour-complete",
         text: "You've completed the Overview page tour! You can now explore the page on your own.",
         buttons: [
            {
               action: function () {
                  // Shepherd adds this method to the button context
                  if (this.back) return this.back()
                  return undefined
               },
               classes: "shepherd-button-secondary q-ml-sm",
               text: "Back",
            },
            {
               action: function () {
                  if (this.complete) return this.complete()
                  return undefined
               },
               classes: "shepherd-button-primary q-ml-sm",
               text: "Complete",
            },
         ],
         floatingUIOptions: standardOffset,
      },
   ],
}
